import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { resendVerificationEmail } from "../features/user/userSlice";
import { 
  BsEnvelope, 
  BsArrowRight, 
  BsCheckCircle 
} from "react-icons/bs";

// Validation schema
let resendEmailSchema = yup.object({
  email: yup
    .string()
    .required("Email is Required")
    .email("Email Should be valid")
    .max(100, "Email cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address"
    ),
});

const ResendVerificationEmail = () => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: resendEmailSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      setErrorMessage("");
      dispatch(resendVerificationEmail(values));
    },
  });

  // Handle response from dispatch
  React.useEffect(() => {
    if (authState) {
      setIsLoading(false);
      if (authState.isSuccess && authState.verificationEmail) {
        setShowSuccess(true);
        setErrorMessage("");
      } else if (authState.isError) {
        setShowSuccess(false);
        const errorMsg = authState?.message?.response?.data?.message || 
                        authState?.message?.message || 
                        "An error occurred while resending verification email. Please try again later.";
        setErrorMessage(errorMsg);
      }
    }
  }, [authState]);

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Resend Verification Email</h3>
      
      {showSuccess ? (
        <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <BsCheckCircle className="text-green-500 text-3xl" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Email Sent Successfully!</h3>
          <p className="text-gray-600">
            Please check your email inbox for the verification link.
          </p>
        </div>
      ) : (
        <>
          {/* Error Alert */}
          {errorMessage && (
            <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <BsEnvelope className="text-red-500 text-lg" />
                </div>
                <input
                  type="email"
                  name="email"
                  id="email"
                  placeholder="Enter your email address"
                  value={formik.values.email}
                  onChange={formik.handleChange("email")}
                  onBlur={formik.handleBlur("email")}
                  className={`block w-full pl-12 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    formik.touched.email && formik.errors.email
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                />
              </div>
              {formik.touched.email && formik.errors.email && (
                <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Sending Email...</span>
                </>
              ) : (
                <>
                  <span>Resend Verification Email</span>
                  <BsArrowRight className="text-xl" />
                </>
              )}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ResendVerificationEmail;